import React, { useCallback, useEffect, useState } from "react";
import ScrollFade from "../ScrollFade";
import axios from "axios";
import { toast } from "sonner";
import { DollarSign, Crown, BadgeCheck, RefreshCw, Loader2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37", RED = "#C41E24";
const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const fmt = (d) => { if (!d) return "—"; const t = new Date(d + (d.length === 10 ? "T12:00:00Z" : "")); return isNaN(t) ? d : t.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };

// Coach → Content: revenue line items (pre-launch vs post-launch) + the paid Blueprint Members ledger.
export default function BlueprintMembers() {
  const token = localStorage.getItem("coachToken");
  const [rev, setRev] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(() => axios.get(`${API}/coach/business-pulse`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setRev(data.revenue)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  const resync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/coach/business-pulse/prelaunch/apply`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Pre-launch ledger synced · ${data.purchases} purchases · ${data.linked_players} linked to player accounts`);
      load();
    } catch { toast.error("Sync failed."); }
    setSyncing(false);
  };
  const pre = rev?.prelaunch, post = rev?.post_launch;
  return (
    <div data-testid="blueprint-members" className="rounded-lg border border-[#D4AF37]/50 bg-[#0d0d0d] p-4 mb-5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 font-headings text-lg uppercase tracking-wide"><DollarSign size={16} className="text-[#D4AF37]" /> Revenue &amp; Blueprint Members</div>
        <button data-testid="blueprint-resync" onClick={resync} disabled={syncing} className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm border border-white/15 text-white/70 hover:text-white disabled:opacity-60">
          {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Re-sync pre-launch Stripe ledger
        </button>
      </div>
      <p className="text-xs text-white/55 mt-1 mb-4">Paid Stripe charges only. Test accounts (Dwain O Barker) are excluded from every revenue number. Pre-launch = charged before <span className="text-white/80">Oct 1, 2026</span>.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5" data-testid="revenue-line-items">
        <div data-testid="revenue-prelaunch" className="rounded-lg border p-3" style={{ borderColor: `${GOLD}66`, background: `${GOLD}10` }}>
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: GOLD }}>Pre-launch revenue</div>
          <div className="font-cover text-3xl leading-none mt-1" data-testid="revenue-prelaunch-value">{money(pre?.total)}</div>
          <div className="text-[10px] text-white/45 mt-1">{pre?.count ?? 0} Blueprint sales · collected before Oct 1 · early access</div>
        </div>
        <div data-testid="revenue-postlaunch" className="rounded-lg border border-white/10 bg-[#141414] p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-white/50">Post-launch revenue</div>
          <div className="font-cover text-3xl leading-none mt-1" data-testid="revenue-postlaunch-value">{money(post?.total)}</div>
          <div className="text-[10px] text-white/45 mt-1">{post?.count ?? 0} paid since Oct 1 · compare vs. ad spend in Ad Attribution</div>
        </div>
        <div data-testid="revenue-total" className="rounded-lg border p-3" style={{ borderColor: `${RED}66`, background: `${RED}10` }}>
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#ff8a8e]">Total revenue</div>
          <div className="font-cover text-3xl leading-none mt-1" data-testid="revenue-total-value">{money(rev?.total)}</div>
          <div className="text-[10px] text-white/45 mt-1">{rev?.count ?? 0} paid transactions · all tiers</div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/50 mb-2"><Crown size={12} style={{ color: GOLD }} /> Blueprint Members · {rev?.members?.length ?? 0}</div>
      {!rev ? <div className="text-xs text-white/40">Loading…</div> : rev.members.length === 0 ? <div data-testid="blueprint-members-empty" className="text-xs text-white/40">No paid Blueprint purchases recorded yet.</div> : (
        <ScrollFade testId="blueprint-members-scroll" className="-mx-1">
          <table className="w-full text-xs min-w-[560px]" data-testid="blueprint-members-table">
            <thead><tr className="text-left text-[9px] uppercase tracking-widest text-white/40 border-b border-white/10">
              <th className="py-2 px-1">Member</th><th className="py-2 px-1">Status</th><th className="py-2 px-1 text-right">Paid</th><th className="py-2 px-1">Purchase date</th><th className="py-2 px-1">Account</th>
            </tr></thead>
            <tbody>
              {rev.members.map((m, i) => (
                <tr key={`${m.name}-${i}`} data-testid={`blueprint-member-${i}`} className="border-b border-white/5">
                  <td className="py-2 px-1">
                    <div className="font-semibold text-white">{m.name}</div>
                    <div className="text-[10px] text-white/40">{m.membership}{m.email ? ` · ${m.email}` : ""}</div>
                  </td>
                  <td className="py-2 px-1">
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/40"><BadgeCheck size={10} /> Paid</span>
                    {m.prelaunch && <span data-testid={`blueprint-member-prelaunch-${i}`} className="ml-1 inline-flex text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border" style={{ color: GOLD, borderColor: `${GOLD}66`, background: `${GOLD}14` }}>Pre-launch · Early Access</span>}
                  </td>
                  <td className="py-2 px-1 text-right font-mono text-white">{money(m.amount)}</td>
                  <td className="py-2 px-1 text-white/70">{fmt(m.date)}</td>
                  <td className="py-2 px-1 text-[10px]">{m.player_id ? (m.claimed ? <span className="text-[#22C55E]">Claimed</span> : <span className="text-white/50">Linked · not claimed</span>) : <span className="inline-flex items-center gap-1 text-white/35"><Lock size={9} /> No account yet</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollFade>
      )}
    </div>
  );
}
